import React, { useCallback, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import {
  authController,
  ChildAuthComponentProps,
} from "../../../../application/auth";
import ReCaptchaProvider from "../../ReCaptchaProvider";
import ReCaptchaCheckbox from "../../ReCaptchaCheckbox";
import ErrorContainer from "../../errorContainer";
import ThrottleButton from "../../throttleButton";
import logger from "../../../../utils/logger";
import UseRecaptchaService from "../../../../services/recaptcha.service";

const isRecaptchaEnabled = UseRecaptchaService.isEnabled();

const PasswordlessEmailFormContent: React.FC<ChildAuthComponentProps> = ({
  useEmailState,
  useErrorState,
  useViewState,
}) => {
  const [email, setEmail] = useEmailState;
  const [, setErrorMessage] = useErrorState;
  const [, setViewState] = useViewState;
  const [emailError, setEmailError] = useState<string>("");
  const [isEnterThrottled, setIsEnterThrottled] = useState<boolean>(false);
  const [showChallenge, setShowChallenge] = useState<boolean>(false);
  const [challengeAttempt, setChallengeAttempt] = useState<number>(0);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const throttleTimeInMs = 1000;

  const handleError = useCallback(
    (error: unknown) => {
      if (error instanceof Error) {
        if (error.cause === "email") {
          setEmailError(error.message);

          return;
        }

        setErrorMessage(error.message);
      } else {
        setErrorMessage(error as string);
      }
    },
    [setErrorMessage],
  );

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();

      try {
        setErrorMessage("");

        let recaptchaToken: string | undefined = undefined;
        if (isRecaptchaEnabled && executeRecaptcha) {
          recaptchaToken = await executeRecaptcha("passwordless_email");
        } else if (isRecaptchaEnabled && !executeRecaptcha) {
          logger.error(
            "reCAPTCHA is enabled but executeRecaptcha is not available",
          );
          return;
        }

        await authController.sendOTPCode(email, recaptchaToken);

        setViewState("otp");
      } catch (error: unknown) {
        if (error instanceof Error && error.cause === "challenge") {
          // Backend asked for a visible puzzle — render the checkbox.
          setShowChallenge(true);
          setErrorMessage("Please complete the verification to continue.");
          return;
        }

        handleError(error);
      }
    },
    [executeRecaptcha, email, setErrorMessage, setViewState, handleError],
  );

  const handleChallengeSolved = useCallback(
    async (challengeToken: string) => {
      try {
        setErrorMessage("");

        await authController.sendOTPCode(email, undefined, challengeToken);

        setShowChallenge(false);
        setViewState("otp");
      } catch (error: unknown) {
        // The solved token was rejected (spent/low score). Remount a fresh
        // widget so the user can retry instead of staring at a dead checkbox.
        setChallengeAttempt((n) => n + 1);
        handleError(error);
      }
    },
    [email, setErrorMessage, setViewState, handleError],
  );

  const onChangeEmail = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.preventDefault();

      if (!event.target.value) {
        setEmailError("Email is required");
      } else {
        setEmailError("");
      }

      setEmail(event.target.value);
    },
    [setEmail],
  );

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();

        if (!isEnterThrottled) {
          setIsEnterThrottled(true);
          handleSubmit(event as unknown as React.FormEvent);
          setTimeout(() => setIsEnterThrottled(false), throttleTimeInMs);
        }
      }
    },
    [isEnterThrottled, handleSubmit],
  );

  return (
    <>
      <h2 className="auth-header">Get a login code emailed to you</h2>
      <div className="auth-form-container">
        <form className="auth-form">
          <div className="field">
            <label htmlFor="email" className="label">
              Your email:
            </label>
            <input
              id="email"
              type="email"
              className="input"
              value={email || ""}
              onKeyDown={handleKeyPress}
              onChange={onChangeEmail}
              disabled={showChallenge}
            />
            {emailError && <p className="error-message">{emailError}</p>}
          </div>

          {showChallenge && (
            <div className="field">
              <ReCaptchaCheckbox
                key={challengeAttempt}
                onSolved={handleChallengeSolved}
                onExpired={() => {
                  setChallengeAttempt((n) => n + 1);
                  setErrorMessage(
                    "Verification expired. Please check the box again.",
                  );
                }}
              />
            </div>
          )}

          <div className="buttons">
            <ThrottleButton
              throttleTimeInMs={1000}
              type={"button"}
              disabled={!email || isEnterThrottled}
              classList="primary-button"
              onClick={handleSubmit}
            >
              Send me a code
            </ThrottleButton>
          </div>
        </form>

        <ErrorContainer useErrorState={useErrorState} />
      </div>
    </>
  );
};

const PasswordlessEmailForm: React.FC<ChildAuthComponentProps> = (props) => (
  <ReCaptchaProvider>
    <PasswordlessEmailFormContent {...props} />
  </ReCaptchaProvider>
);

export default PasswordlessEmailForm;
